import React from 'react';
import './MainPage.scss';
import { useAppDispatch, useAppSelector } from 'store/store';
import { asyncFetchElections } from 'store/reducers/elections/thunks';
import { IElection } from 'store/reducers/elections/types';
import { isStudent } from 'js/utils';
import { facultyGradientByName } from 'theme/faculties';
import ElectionCard from 'components/redesign/ElectionCard';
import { Link, useLocation } from 'react-router-dom';
import { ElectionCardData } from 'components/redesign/ElectionCard/ElectionCard';
import FaqBlock from 'components/redesign/FaqBlock';

function toCardData(election: IElection): ElectionCardData {
    return {
        id: election.id,
        title: election.name,
        facultyNames: [
            election.faculty ? election.faculty.short_name : 'СПбГУТ',
            election.faculty ? election.faculty.name : undefined,
        ],
        dateStart: election.date_start,
        dateEnd: election.date_end,
        candidatesCount: election.candidates.length,
        pictures: election.candidates.map(candidate => candidate.picture),
    };
}

function MainPage(): React.ReactElement {
    const dispatch = useAppDispatch();
    const { search } = useLocation();
    const { elections } = useAppSelector(state => state.electionsStore);
    const { user } = useAppSelector(state => state.userStore);
    const [loadState, setLoadState] = React.useState<
        'loading' | 'ready' | 'error'
    >('loading');
    const [request, retry] = React.useReducer(value => value + 1, 0);

    React.useEffect(() => {
        let active = true;
        setLoadState('loading');
        dispatch(asyncFetchElections())
            .unwrap()
            .then(
                () => {
                    if (active) setLoadState('ready');
                },
                () => {
                    if (active) setLoadState('error');
                }
            );
        return () => {
            active = false;
        };
    }, [dispatch, request]);

    const subdivisions = elections;

    const own =
        user && isStudent(user)
            ? subdivisions.find(
                  item => item.faculty?.id === user.group.faculty.id
              )
            : undefined;

    const rest = own
        ? subdivisions.filter(election => election.id !== own.id)
        : subdivisions;

    const ownPortraits = own?.candidates.map(candidate => {
        const name = `${candidate.first_name} ${candidate.last_name}`;
        const url = `/elections/${own.id}/candidates/${candidate.id}${search}`;
        return (
            <Link
                className="rd-own-portrait"
                key={candidate.id}
                to={url}
                aria-label={`${name}: программа`}
                title={name}
            >
                <img src={candidate.picture} alt="" />
                <span className="rd-own-portrait__name">
                    <span>{candidate.first_name}</span>
                    <strong>{candidate.last_name}</strong>
                </span>
            </Link>
        );
    });

    let cardsContent: React.ReactNode = null;
    if (loadState === 'error') {
        cardsContent = (
            <div className="rd-main-state" role="alert">
                <p>
                    Не удалось загрузить выборы. Проверь соединение и попробуй
                    ещё раз.
                </p>
                <button
                    type="button"
                    className="rd-btn rd-btn--green"
                    onClick={retry}
                >
                    Повторить
                </button>
            </div>
        );
    } else if (loadState === 'loading') {
        cardsContent = (
            <div className="rd-main-state" role="status">
                Загружаем выборы…
            </div>
        );
    } else if (subdivisions.length === 0) {
        cardsContent = (
            <div className="rd-main-state">Выборы пока не опубликованы.</div>
        );
    } else {
        cardsContent = (
            <>
                {own && (
                    <section className="rd-block">
                        <h2 className="rd-h2">
                            Ваш факультет — {own.faculty?.name}
                        </h2>
                        <div
                            className="rd-own-layout"
                            style={{
                                background: facultyGradientByName(
                                    own.faculty?.name,
                                    own.faculty?.short_name
                                ),
                            }}
                        >
                            <ElectionCard data={toCardData(own)} embedded />
                            <div className="rd-own-portraits">
                                {ownPortraits}
                            </div>
                        </div>
                    </section>
                )}
                {rest.length > 0 && (
                    <section className="rd-block">
                        {own && <h2 className="rd-h2">Другие выборы</h2>}
                        <div className="rd-grid">
                            {rest.map(item => (
                                <ElectionCard
                                    key={item.id}
                                    data={toCardData(item)}
                                />
                            ))}
                        </div>
                    </section>
                )}
            </>
        );
    }

    return (
        <div className="rd-container rd-sections">
            <section className="rd-block">
                <h1 className="rd-h1">Выборы</h1>
                {cardsContent}
            </section>

            <FaqBlock />
        </div>
    );
}

export default React.memo(MainPage);
